/* ---------------------------------------------------------------------------
   Runtime content loader.

   The site is authored statically in colleges.js / imagery.js and elsewhere.
   This provider fetches the CMS document from GET /content and layers it *over*
   those bundled defaults, so:

     - before the backend is provisioned (or if it is unreachable) the site
       renders exactly as it does today from the bundled data, and
     - once content is seeded, any section present in the CMS document wins.

   Components opt in section by section with useSection("key", bundledDefault) —
   no component breaks until it is migrated, and none regresses after.

   Image slots come back from the API already resolved to { src, key, alt } (the
   read Lambda turns stored { $img } refs into presigned URLs), so components can
   use `.src` directly.
--------------------------------------------------------------------------- */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { API_BASE } from "../config";

const ContentContext = createContext({ content: {}, loading: true, error: null });

export function ContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/content`, { mode: "cors" });
        if (!res.ok) throw new Error(`content ${res.status}`);
        const doc = await res.json();
        if (alive) setContent(doc && typeof doc === "object" ? doc : {});
      } catch (err) {
        // Network/API not ready — fall back silently to bundled defaults.
        if (alive) setError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo(() => ({ content, loading, error }), [content, loading, error]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

/** Raw access to the whole loaded document + status. */
export function useContent() {
  return useContext(ContentContext);
}

/**
 * A single section's content with a bundled fallback.
 * Returns `fallback` until the CMS document provides `content[key]`.
 */
export function useSection(key, fallback) {
  const { content } = useContext(ContentContext);
  const value = content ? content[key] : undefined;
  return value === undefined || value === null ? fallback : value;
}
